import {
  Add as AddIcon,
  Edit as EditIcon,
  FolderOpen as FolderOpenIcon,
  NavigateNext as BreadcrumbSeparator,
  PersonAdd as ShareIcon,
} from "@mui/icons-material";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ResearchProject } from "../../services/researchApi";
import ActiveRunsIndicator from "./ActiveRunsIndicator";

interface ResearchWorkflowHeaderProps {
  projectName: string;
  trackName?: string;
  project?: ResearchProject | null;
  onShareClick?: () => void;
  onLoadProject: () => void;
  onNewProject: () => void;
  onRenameTrack?: (newName: string) => void;
  isTrack?: boolean;
}

const ResearchWorkflowHeader: React.FC<ResearchWorkflowHeaderProps> = ({
  projectName,
  trackName,
  project,
  onShareClick,
  onLoadProject,
  onNewProject,
  onRenameTrack,
  isTrack,
}) => {
  const { user: currentUser, hasFeature } = useAuth();
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  const handleOpenRename = () => {
    setRenameValue(trackName || "");
    setRenameOpen(true);
  };

  const handleRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== trackName && onRenameTrack) {
      onRenameTrack(trimmed);
    }
    setRenameOpen(false);
  };

  const isOwner = project?.permission_level === "OWNER";
  const collaborators = project?.shared_with ?? [];

  const getInitials = (user: {
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) return user.first_name[0].toUpperCase();
    return user.email.slice(0, 2).toUpperCase();
  };

  const getDisplayName = (user: {
    first_name: string;
    last_name: string;
    email: string;
    permission_level: string;
  }) => {
    const name =
      user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.email;
    return `${name} (${user.permission_level.toLowerCase().replace(/_/g, " ")})`;
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          px: 0,
          py: 0,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          display: "flex",
          alignItems: "center",
          minHeight: 80,
        }}
      >
        {/* Left zone: project management buttons — matches sidebar width */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            gap: 1,
            px: 2,
            borderRight: 1,
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<FolderOpenIcon />}
            onClick={onLoadProject}
            sx={{ whiteSpace: "nowrap", flex: 1 }}
          >
            Load Project
          </Button>
          {/* research_viewer-only accounts cannot create projects */}
          {hasFeature("research") && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onNewProject}
              sx={{ whiteSpace: "nowrap", flex: 1 }}
            >
              New Project
            </Button>
          )}
        </Box>

        {/* Center: breadcrumb title */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 3,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="600"
            noWrap
            color="text.primary"
            sx={{ flexShrink: 1, minWidth: 0 }}
          >
            {projectName}
          </Typography>
          {trackName && (
            <>
              <BreadcrumbSeparator
                sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  noWrap
                  sx={{ flexShrink: 1, minWidth: 0 }}
                >
                  {trackName}
                </Typography>
                {isTrack && onRenameTrack && (
                  <IconButton
                    size="small"
                    onClick={handleOpenRename}
                    sx={{
                      color: "text.secondary",
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Right zone: active runs + collaborators + share */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2 }}>
          <ActiveRunsIndicator />
          {collaborators.length > 0 && (
            <AvatarGroup
              max={5}
              sx={{
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderColor: "background.paper",
                },
              }}
            >
              {collaborators.map((user, idx) => (
                <Tooltip key={idx} title={getDisplayName(user)}>
                  <Avatar
                    sx={{
                      bgcolor:
                        user.email === currentUser?.email
                          ? "primary.main"
                          : "grey.600",
                    }}
                  >
                    {getInitials(user)}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          )}
          {isOwner && onShareClick && (
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={onShareClick}
              sx={{ whiteSpace: "nowrap" }}
            >
              Share
            </Button>
          )}
        </Box>
      </Paper>

      {/* Rename track modal */}
      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#1e1e1e" } }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          Rename Track
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <TextField
            autoFocus
            fullWidth
            label="Track Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRename}
            disabled={!renameValue.trim() || renameValue.trim() === trackName}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResearchWorkflowHeader;
